/**
 * RoundDashboardScreen — route `/rounds/:roundId` (reviewer+).
 *
 * Renders the round-end dashboard from `getRoundReport` (GET
 * /rounds/{round_id}/report → BatchReport): KPI cards (approved / rejected /
 * human_review / system_errors with the server's `pct_*` percentages, plus the
 * conditional "No resueltos" and "QR sin decodificar" tiles — the latter from
 * `advisory_flags.QR_DECODE_PENDING`, the per-round number the QR decode
 * ladder is measured by), a `rejection_breakdown` bar chart (recharts), and
 * the system-errors list, plus contextual nav CTAs.
 *
 * CRITICAL branch: the report 404s mid-round (codes ROUND_NOT_FOUND /
 * REPORT_UNAVAILABLE) until the round is `completed`. We intercept a 404 (by
 * HTTP status, which covers BOTH codes) and show an explicit "report not yet
 * available" empty state with a manual refresh ("poll") and a link to the live
 * monitor. A 502 REPORT_UNAVAILABLE (corrupt snapshot) is a REAL error and
 * falls through to ProblemAlert. Auth (401/403) is handled by the route guards.
 */

import { useMemo } from "react";
import type { ReactNode } from "react";
import { Link as RouterLink, useLocation, useParams } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import GavelIcon from "@mui/icons-material/Gavel";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import DownloadIcon from "@mui/icons-material/Download";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import RefreshIcon from "@mui/icons-material/Refresh";

import { api, useApiQuery } from "../api/useApi";
import { IS_DEMO } from "../api/client";
import type { BatchReport } from "../api/client";
import { FAILURE_CODE_LABEL, labelOrCode } from "../api/labels";
import { useAuth } from "../auth/AuthProvider";
import { roleAtLeast } from "../auth/roles";
import { Loading } from "../components/Loading";
import { ProblemAlert } from "../components/ProblemAlert";
import { EmptyState } from "../components/EmptyState";
import { RoundTabs } from "../components/RoundTabs";
import { RoundOverview } from "../components/RoundOverview";
import { CloseRoundButton } from "../components/CloseRoundButton";
import {
  RoundExpedientesBrowser,
  type DecisionFilter,
} from "../components/RoundExpedientesBrowser";
import { classifyReportGap } from "./reportGap";
import { useTableroView } from "./roundIndex/useTableroView";
import { describeSystemError, describeSystemErrorAction } from "./systemErrorText";

/**
 * Synthetic report for DEMO mode (no API base URL configured). Sourced up front
 * from `IS_DEMO` — NEVER used as an error fallback (mock-on-error is forbidden).
 */
const DEMO_REPORT: BatchReport = {
  summary: {
    total: 1240,
    approved: 1118,
    rejected: 74,
    human_review: 36,
    system_errors: 12,
    pct_approved: 90.16,
    pct_rejected: 5.97,
    pct_human_review: 2.9,
    pct_errors: 0.97,
    unresolved: 0,
  },
  // Advisory flags ride on APPROVED and HUMAN_REVIEW verdicts (mostly APPROVED);
  // the tile below shows the QR_DECODE_PENDING count the ladder is measured by (#307).
  advisory_flags: { QR_DECODE_PENDING: 57 },
  rejection_breakdown: {
    CURP_MISMATCH: 28,
    FACE_NO_MATCH: 19,
    DOC_ILLEGIBLE: 14,
    EXPIRED_DOCUMENT: 8,
    MISSING_PAGE: 5,
  },
  human_review_queue: [],
  system_errors: [
    {
      expediente_id: "EXP-000341",
      error: "OCR timeout (layer 2)",
      action: "MANUAL_REVIEW",
    },
    {
      expediente_id: "EXP-000917",
      error: "Vision adapter 503",
      action: "MANUAL_REVIEW",
    },
  ],
};

/** Format a server-provided percentage. We treat `pct_*` as a 0–100 number. */
function formatPct(pct: number): string {
  return `${pct.toLocaleString("es-MX", { maximumFractionDigits: 1 })} %`;
}

type KpiTone = "success" | "error" | "warning" | "info" | "primary";

function KpiCard({
  label,
  count,
  pct,
  tone,
  icon,
  active = false,
  onClick,
}: {
  label: string;
  count: number;
  pct: number;
  tone: KpiTone;
  icon: ReactNode;
  /** Highlighted (pressed) when this card is the active table filter. */
  active?: boolean;
  /** When set, the card is an interactive status filter for the table below. */
  onClick?: () => void;
}) {
  const theme = useTheme();
  const color =
    tone === "info"
      ? theme.palette.secondary.main
      : tone === "primary"
        ? theme.palette.primary.main
        : theme.palette[tone].main;
  const clickable = Boolean(onClick);
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-pressed={clickable ? active : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      sx={{
        p: 2.5,
        height: "100%",
        cursor: clickable ? "pointer" : "default",
        borderColor: active ? color : "divider",
        bgcolor: active ? alpha(color, 0.08) : "background.paper",
        // Inset ring instead of a thicker border so the active state never
        // shifts the grid layout by a pixel.
        boxShadow: active ? `inset 0 0 0 1px ${color}` : "none",
        transition: theme.transitions.create(
          ["border-color", "background-color", "box-shadow"],
          { duration: theme.transitions.duration.shortest },
        ),
        "&:hover": clickable
          ? { borderColor: color, bgcolor: alpha(color, active ? 0.12 : 0.04) }
          : undefined,
        "&:focus-visible": clickable
          ? { outline: `2px solid ${color}`, outlineOffset: 2 }
          : undefined,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{ color, mb: 1, alignItems: "center" }}
      >
        {icon}
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
      </Stack>
      <Typography variant="h1" sx={{ lineHeight: 1.1 }}>
        {count.toLocaleString("es-MX")}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {formatPct(pct)} del total
      </Typography>
    </Paper>
  );
}

function NavCtas({
  roundId,
  onClosed,
}: {
  roundId: string;
  onClosed?: () => void;
}) {
  const { role } = useAuth();
  const isOperator = roleAtLeast(role, "operator");
  return (
    <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap" }}>
      {/* operator-only — gate so reviewers don't hit a RequireRole block */}
      {isOperator && (
        <Button
          component={RouterLink}
          to={`/rounds/${roundId}/monitor`}
          variant="outlined"
          startIcon={<MonitorHeartIcon />}
        >
          Monitor
        </Button>
      )}
      {isOperator && (
        <Button
          component={RouterLink}
          to={`/rounds/${roundId}/operations`}
          variant="outlined"
        >
          Operaciones
        </Button>
      )}
      {isOperator && (
        <Button
          component={RouterLink}
          to={`/rounds/${roundId}/exports`}
          variant="outlined"
          startIcon={<DownloadIcon />}
        >
          Exportaciones
        </Button>
      )}
      {/* Close the round (operator-only): freezes the report + ends reenvíos. */}
      {isOperator && <CloseRoundButton roundId={roundId} onClosed={onClosed} />}
    </Stack>
  );
}

/**
 * "Report not available" notice, shown ABOVE the expediente browser when the
 * round has no snapshot (issue #217). Deliberately an alert and not an
 * `EmptyState`: the page is not empty — the expedientes are listed below it.
 */
function ReportUnavailableNotice({
  roundId,
  onRetry,
}: {
  roundId: string;
  onRetry: () => void;
}) {
  const { role } = useAuth();
  const isOperator = roleAtLeast(role, "operator");
  return (
    <Alert
      severity="info"
      icon={<HourglassEmptyIcon fontSize="inherit" />}
      sx={{ mb: 3 }}
      action={
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
          <Button size="small" startIcon={<RefreshIcon />} onClick={onRetry}>
            Actualizar
          </Button>
          {isOperator && (
            <Button
              component={RouterLink}
              to={`/rounds/${roundId}/monitor`}
              size="small"
              startIcon={<MonitorHeartIcon />}
            >
              Monitor
            </Button>
          )}
        </Stack>
      }
    >
      <AlertTitle>Indicadores aún no disponibles</AlertTitle>
      Esta ronda todavía no tiene reporte sellado, así que no hay KPIs ni
      desglose de rechazos. Los expedientes recibidos sí se listan abajo. Las
      rondas alimentadas por el widget se procesan expediente por expediente:
      su reporte se sella al correr el lote o al cerrar la ronda.
    </Alert>
  );
}

/**
 * Standalone decision filter for the no-report path (#217). On the normal
 * dashboard the KPI cards ARE the filter; without a report there are no cards,
 * so the browser would otherwise be stuck on "todos". No counts here on
 * purpose — they live in the snapshot we don't have, and a guessed number is
 * worse than none.
 */
function DecisionFilterBar({
  value,
  onChange,
}: {
  value: DecisionFilter;
  onChange: (next: DecisionFilter) => void;
}) {
  const OPTIONS: { value: DecisionFilter; label: string }[] = [
    { value: "ALL", label: "Todos" },
    { value: "APPROVED", label: "Aprobados" },
    { value: "REJECTED", label: "Rechazados" },
    { value: "HUMAN_REVIEW", label: "Revisión humana" },
  ];
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={value}
      // `null` when the active button is clicked again — keep the filter as-is.
      onChange={(_, next: DecisionFilter | null) => next && onChange(next)}
      aria-label="Filtrar expedientes por decisión"
      sx={{ mb: 2, flexWrap: "wrap" }}
    >
      {OPTIONS.map((o) => (
        <ToggleButton key={o.value} value={o.value}>
          {o.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

/** "Round not found" — the 404 where there is nothing to list at all. */
function RoundNotFoundState({ onRetry }: { onRetry: () => void }) {
  return (
    <EmptyState
      icon={<HourglassEmptyIcon fontSize="inherit" />}
      title="Ronda no encontrada"
      description="No existe una ronda con ese identificador. Verifica la liga o vuelve al listado de rondas."
      action={
        <Stack
          direction="row"
          spacing={1.5}
          useFlexGap
          sx={{ flexWrap: "wrap", justifyContent: "center" }}
        >
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={onRetry}
          >
            Actualizar
          </Button>
          <Button component={RouterLink} to="/rounds" variant="outlined">
            Ver rondas
          </Button>
        </Stack>
      }
    />
  );
}

export default function RoundDashboardScreen() {
  const { roundId = "" } = useParams();
  const location = useLocation();
  const theme = useTheme();

  // The KPI cards double as the status filter for the expediente browser
  // below; the selection lives in the URL (`?decision=`, #336) with the rest
  // of the browser's view.
  const { view, update } = useTableroView();
  const statusFilter = view.decision;
  const setStatusFilter = (decision: DecisionFilter) => update({ decision });

  const { data, error, loading, reload, refresh } =
    useApiQuery<BatchReport>(async () => {
      if (IS_DEMO) return DEMO_REPORT;
      const res = await api.GET("/rounds/{round_id}/report", {
        params: { path: { round_id: roundId } },
      });
      // The 200 response declares both application/json (BatchReport) and
      // text/csv (string); without `format` the server returns JSON, so the
      // union resolves to BatchReport here.
      return res.data as BatchReport;
    }, [roundId]);

  // Map of failure code → count → recharts-friendly rows. May be empty.
  const breakdownRows = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.rejection_breakdown)
      .map(([code, count]) => ({
        code,
        // Localized label for the axis/tooltip; falls back to the raw code.
        label: labelOrCode(FAILURE_CODE_LABEL, code),
        count,
      }))
      .sort((a, b) => b.count - a.count);
  }, [data]);

  if (loading) return <Loading label="Cargando el reporte de la ronda…" />;

  // CRITICAL: intercept the 404s BEFORE the generic alert, and tell the two
  // codes apart (#217). A 502 REPORT_UNAVAILABLE (corrupt snapshot) is NOT a
  // 404, so it correctly falls through below.
  const gap = classifyReportGap(error);

  if (gap === "NO_ROUND") {
    return (
      <Box>
        <RoundTabs roundId={roundId} currentPath={location.pathname} />
        <Header roundId={roundId} />
        <RoundNotFoundState onRetry={reload} />
      </Box>
    );
  }

  // The round exists but has no sealed report — permanently so for a widget-fed
  // round, which never runs a batch. The KPI grid and the rejection chart both
  // need the snapshot, but the expediente browser reads its own endpoint, so we
  // degrade to notice + browser instead of hiding the expedientes entirely.
  if (gap === "NO_REPORT") {
    return (
      <Box>
        <RoundTabs roundId={roundId} currentPath={location.pathname} />
        <Header roundId={roundId} />
        <ReportUnavailableNotice roundId={roundId} onRetry={reload} />
        <DecisionFilterBar value={statusFilter} onChange={setStatusFilter} />
        <RoundExpedientesBrowser roundId={roundId} onIndexChange={refresh} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <RoundTabs roundId={roundId} currentPath={location.pathname} />
        <Header roundId={roundId} />
        <ProblemAlert error={error} onRetry={reload} />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box>
        <RoundTabs roundId={roundId} currentPath={location.pathname} />
        <Header roundId={roundId} />
        <EmptyState
          title="Sin datos"
          description="No hay información para esta ronda."
        />
      </Box>
    );
  }

  const s = data.summary;
  const chartColor = theme.palette.error.main;
  // Abandoned reenvíos after a forced close (issue #150). Almost every round has
  // 0, so the tile only appears when there is something to account for.
  const showUnresolved = s.unresolved > 0;
  // Certificados with a QR the offline decode ladder could not read (ADR 0010).
  // Advisory — it never moves a verdict — so it is not a table filter, and it
  // only appears when there is something to account for. Snapshots sealed
  // before the field existed serve `{}`.
  const qrPending = data.advisory_flags?.QR_DECODE_PENDING ?? 0;
  const showQrPending = qrPending > 0;
  const kpiColumns = 5 + (showUnresolved ? 1 : 0) + (showQrPending ? 1 : 0);

  return (
    <Box>
      <RoundTabs roundId={roundId} currentPath={location.pathname} />
      <Header roundId={roundId} />

      {/* KPI cards — "Todos" + the three decision cards double as the status
          filter for the expediente browser below (click to filter). "Errores
          del sistema" and "No resueltos" are separate metrics, not table
          decisions (the browser's `decision` filter doesn't take
          AWAITING_RESUBMISSION), so they stay display-only. */}
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "repeat(3, 1fr)",
            lg: `repeat(${kpiColumns}, 1fr)`,
          },
          mb: 1,
        }}
      >
        <KpiCard
          label="Todos"
          count={s.total}
          pct={100}
          tone="primary"
          icon={<FormatListBulletedIcon fontSize="small" />}
          active={statusFilter === "ALL"}
          onClick={() => setStatusFilter("ALL")}
        />
        <KpiCard
          label="Aprobados"
          count={s.approved}
          pct={s.pct_approved}
          tone="success"
          icon={<CheckCircleOutlineIcon fontSize="small" />}
          active={statusFilter === "APPROVED"}
          onClick={() => setStatusFilter("APPROVED")}
        />
        <KpiCard
          label="Rechazados"
          count={s.rejected}
          pct={s.pct_rejected}
          tone="error"
          icon={<HighlightOffIcon fontSize="small" />}
          active={statusFilter === "REJECTED"}
          onClick={() => setStatusFilter("REJECTED")}
        />
        <KpiCard
          label="Revisión humana"
          count={s.human_review}
          pct={s.pct_human_review}
          tone="warning"
          icon={<GavelIcon fontSize="small" />}
          active={statusFilter === "HUMAN_REVIEW"}
          onClick={() => setStatusFilter("HUMAN_REVIEW")}
        />
        <KpiCard
          label="Errores del sistema"
          count={s.system_errors}
          pct={s.pct_errors}
          tone="info"
          icon={<ReportProblemOutlinedIcon fontSize="small" />}
        />
        {showUnresolved && (
          <KpiCard
            label="No resueltos"
            count={s.unresolved}
            // The contract carries no pct_unresolved; derive it like the others.
            pct={s.total > 0 ? (s.unresolved / s.total) * 100 : 0}
            tone="info"
            icon={<HourglassEmptyIcon fontSize="small" />}
          />
        )}
        {showQrPending && (
          <KpiCard
            label="QR sin decodificar"
            count={qrPending}
            pct={s.total > 0 ? (qrPending / s.total) * 100 : 0}
            tone="info"
            icon={<QrCode2Icon fontSize="small" />}
          />
        )}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Total de expedientes procesados: {s.total.toLocaleString("es-MX")}
      </Typography>

      {/* AI overview ("Resumen IA") — a short, cached Gemini recap of the round,
          shown above the grid. Only rendered for completed rounds (the report
          loaded), and hides itself if the overview isn't available. */}
      <RoundOverview roundId={roundId} />

      {/* Whole-round expediente browser — every expediente, any status. Its
          status filter is driven by the KPI cards above. */}
      {/* When the index probe sees a change (new run, adjudication), the report
          is refreshed in the background so the cards agree with the table. */}
      <RoundExpedientesBrowser roundId={roundId} onIndexChange={refresh} />

      {/* Rejection breakdown chart */}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
        <Typography variant="h3" sx={{ mb: 0.5 }}>
          Desglose de rechazos
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Conteo de expedientes por motivo de rechazo.
        </Typography>
        {breakdownRows.length === 0 ? (
          <EmptyState
            title="Desglose no disponible"
            description="El desglose por motivo de rechazo no está disponible para esta ronda."
          />
        ) : (
          <Box sx={{ width: "100%", height: 360 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={breakdownRows}
                margin={{ top: 8, right: 16, left: 0, bottom: 88 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="label"
                  angle={-35}
                  textAnchor="end"
                  interval={0}
                  height={110}
                  tick={{ fontSize: 12 }}
                />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <RechartsTooltip
                  formatter={(value) => [
                    Number(value).toLocaleString("es-MX"),
                    "Expedientes",
                  ]}
                />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {breakdownRows.map((row) => (
                    <Cell key={row.code} fill={chartColor} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Paper>

      {/* System errors — only shown when there are any (an error-free round
          hides the panel entirely rather than showing an empty state). */}
      {data.system_errors.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: "center" }}>
            <Typography variant="h3">Errores del sistema</Typography>
            <Chip size="small" color="error" label={data.system_errors.length} />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Expedientes que no se pudieron procesar y quedaron sin veredicto. Son
            fallas de expedientes individuales: el resto de la ronda se procesó
            con normalidad. Si alguno ya se volvió a procesar, su veredicto nuevo
            aparece en la lista de expedientes.
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Expediente</TableCell>
                  <TableCell>Error</TableCell>
                  <TableCell>Acción</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.system_errors.map((e) => (
                  <TableRow key={e.expediente_id}>
                    <TableCell sx={{ fontFamily: "monospace" }}>
                      {e.expediente_id}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {describeSystemError(e.error).title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {describeSystemError(e.error).detail}
                      </Typography>
                    </TableCell>
                    <TableCell>{describeSystemErrorAction(e.action)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Divider sx={{ mb: 2 }} />
      <NavCtas roundId={roundId} onClosed={reload} />
    </Box>
  );
}

function Header({ roundId }: { roundId: string }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h1">Tablero de la ronda</Typography>
      <Typography variant="body2" color="text.secondary">
        Ronda {roundId} — resumen de KPIs, desglose de rechazos y errores del
        sistema.
      </Typography>
    </Box>
  );
}
